import PropTypes from "prop-types";
import * as React from "react";
import { Button, Modal } from "react-ui";

import { PathResolver } from "../../withResolvePaths";
import { Header } from "../../FormParts/Header/Header";
import { isChildOfType, nullElement } from "../../TypingUtils";
import { Body } from "../Body/Body";
import { Footer } from "../Footer/Footer";
import { RedefineViewModelIndex } from "../../RedefinedViewModelIndexContext";
import { IControlRegistry } from "../../../Engine/Core/ComponentHandleRegistry/IControlRegistry";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { nextTick } from "../../../../../Common/AsyncUtils";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { track } from "../../Internals/Metrics/MetricsProvider";
import { MODAL_FORM_ACCEPT, MODAL_FORM_CANCEL, MODAL_FORM_OPEN, MODAL_FORM_REMOVE } from "../../metricsAction";

import { ModalFormCancel, ModalFormConfirmContainer as ModalFormConfirm } from "./ModalFormButtons";
import { DefaultFooterContainer } from "./ModalForm.styles";
import { IModalFormsRegistry } from "./ModalFormsContext";

export interface OwnModalFormProps {
    header?: string;
    footerPanel?: boolean;
    children: React.ReactNode;
    enabledBackgroundClick?: boolean;
    width?: string | number;
    handleClose: () => void;
    verifiablePaths: AbsoluteModelPath[];
    resolvePath: PathResolver;
    doNotValidateFormOnStart?: boolean;
    redefineModel: () => void;
    mergeRedefinedModel: () => void;
    dropRedefinedModel: () => void;
    removeModelFields: () => void;
    onValidate: () => Promise<string | undefined>;
    controlRegistry: IControlRegistry;
    redefinedViewModelIndex: number | undefined;
    modalRegistry: IModalFormsRegistry;
    modalId: string;
    disableFocusLock?: boolean;
    bindingPath?: AbsoluteResolvedModelPath;
}

export class ModalForm extends React.Component<OwnModalFormProps> {
    public static childContextTypes = {
        handleCancel: PropTypes.func,
        handleConfirm: PropTypes.func,
        handleRemove: PropTypes.func,
    };

    public getChildContext = () => ({
        handleCancel: this.handleCancel,
        handleConfirm: this.handleConfirm,
        handleRemove: this.handleRemove,
    });

    public UNSAFE_componentWillMount(): void {
        this.props.redefineModel();
    }

    public async componentDidMount() {
        await this.props.modalRegistry.publish("afterOpen", this.props.modalId);
        this.sendMetrics(MODAL_FORM_OPEN);
    }

    public handleCancel = async () => {
        this.props.handleClose();
        // NOTE Состояние от dropRedefinedModel доходит раньше чем закрывается форма. Я просто тороплюсь и не хочу разбираться
        await nextTick();
        this.props.dropRedefinedModel();
        this.sendMetrics(MODAL_FORM_CANCEL);
    };

    public handleConfirm = async (): Promise<boolean> => {
        const errorPath = await this.props.onValidate();
        if (errorPath) {
            const control = IterUtils.last(this.props.controlRegistry.getComponentHandles(errorPath) ?? []);
            setTimeout(() => {
                // NOTE setTimeout тут, чтобы фокус сработал после изменения состояния компонента
                if (control == undefined) {
                    return;
                }
                control.focus();
            }, 0);
            return false;
        } else {
            this.props.handleClose();
            this.props.mergeRedefinedModel();
            this.sendMetrics(MODAL_FORM_ACCEPT);

            return true;
        }
    };

    public handleRemove = async (): Promise<boolean> => {
        try {
            this.props.removeModelFields();
            this.props.handleClose();
            this.sendMetrics(MODAL_FORM_REMOVE);
            return true;
        } catch {
            return false;
        }
    };

    public render(): JSX.Element {
        let header: React.ReactChild | undefined;
        let body: React.ReactChild | null = nullElement;
        let footer: React.ReactChild | undefined;

        React.Children.forEach(this.props.children ?? [], child => {
            if (isChildOfType(child, Header)) {
                header = child.props.children;
            } else if (isChildOfType(child, Body)) {
                body = child;
            } else if (isChildOfType(child, Footer)) {
                footer = child;
            }
        });

        return (
            <TestPageObjectHandle
                id={`Modal/${this.getRedefinedViewModelIndex()}`}
                pageObject={{
                    getType: () => "Modal",
                    close: () => this.handleCancel(),
                }}>
                <RedefineViewModelIndex redefinedViewModelIndex={this.getRedefinedViewModelIndex()}>
                    <Modal
                        onClose={this.handleCancel}
                        ignoreBackgroundClick={!this.props.enabledBackgroundClick}
                        disableFocusLock={this.props.disableFocusLock}
                        width={this.props.width}>
                        {header || this.props.header ? (
                            <Modal.Header>{header || this.props.header}</Modal.Header>
                        ) : (
                            nullElement
                        )}
                        <Modal.Body>{body}</Modal.Body>
                        <Modal.Footer panel={this.props.footerPanel}>
                            {footer ? footer : this.renderFooter()}
                        </Modal.Footer>
                    </Modal>
                </RedefineViewModelIndex>
            </TestPageObjectHandle>
        );
    }

    private renderFooter(): JSX.Element {
        return (
            <DefaultFooterContainer>
                <ModalFormConfirm>
                    <Button use="primary" size="medium">
                        Сохранить
                    </Button>
                </ModalFormConfirm>
                <ModalFormCancel>
                    <Button size="medium">Отменить</Button>
                </ModalFormCancel>
            </DefaultFooterContainer>
        );
    }

    private getRedefinedViewModelIndex(): number {
        const redefinedViewModelIndex = this.props.redefinedViewModelIndex;
        return redefinedViewModelIndex === undefined ? 0 : redefinedViewModelIndex + 1;
    }

    private sendMetrics(actionName: string) {
        if (!this.props.bindingPath) {
            return;
        }

        track("candy", actionName, { path: this.props.bindingPath });
    }
}
